import type { PipelineValidationIssue } from './validatePipeline';
import { CONTRACT_REF_PATTERN, TOOL_NAME_PATTERN } from '../../api/pipelineRefs';
import { isRecord } from '../../api/typeHelpers';

// The messages state each rule and point at the builder list that offers valid
// values; they never cite a real tool or clip style (swappable providers).

/** Mirrors Go validateToolConfig (internal/pipeline/validation/tool.go:21-33). */
export function validateToolCheckpoint(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const tool: Record<string, unknown> = isRecord(cp.tool) ? cp.tool : {};
  const name = typeof tool.name === 'string' ? tool.name.trim() : '';
  if (!name) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" is a tool checkpoint but names no tool — pick one under Tool Settings.`,
    });
  } else if (!TOOL_NAME_PATTERN.test(name)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}": "${name}" is not a valid tool name — use lower-case words joined by dots, as the names in the Tool Settings list are.`,
    });
  }
  if (typeof tool.timeout_s === 'number' && tool.timeout_s < 0) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}": tool timeout_s cannot be negative.` });
  }
}

/** Mirrors Go's validateOutputContract (internal/pipeline/validation/tool.go). */
export function validateOutputContract(
  cp: Record<string, unknown>,
  type: string,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const ref = typeof cp.output_contract === 'string' ? cp.output_contract.trim() : '';
  const render = typeof cp.render_style === 'string' ? cp.render_style.trim() : '';
  if (render && !ref) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" has a render_style but no output_contract — the render style renders a contract’s data.`,
    });
    return;
  }
  if (render && !CONTRACT_REF_PATTERN.test(render)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}": render_style "${render}" must look like <clipstyle>@<version> — pick one from the Render style list.`,
    });
  }
  if (!ref) return;
  if (type !== 'prompt') {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" is a ${type} checkpoint with an output_contract — only prompt checkpoints can have one.`,
    });
    return;
  }
  if (cp.review !== undefined && cp.review !== null) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" has both an output_contract and a Reviewer — the contract is the check, so remove one of them.`,
    });
  }
  if (!CONTRACT_REF_PATTERN.test(ref)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}": output_contract "${ref}" must look like <clipstyle>@<version> — pick one from the Output contract list.`,
    });
  }
}

/**
 * output_fields (mirrors Go validation.validateOutputFields): prompt
 * checkpoints only, not beside an output_contract (the contract is its own
 * check) or a Reviewer (its regeneration would bypass this one), plain JSON
 * keys, a known type ("string" or "string_list", AV-1) and a known format,
 * which applies to strings only. Patterns are RE2 and checked by the engine.
 */
export function validateOutputFields(
  cp: Record<string, unknown>,
  type: string,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const fields = cp.output_fields;
  if (!fields || typeof fields !== 'object' || Array.isArray(fields) || Object.keys(fields).length === 0) return;
  const error = (message: string) => issues.push({ level: 'error', checkpointId, message });
  if (type !== 'prompt') {
    error(`Checkpoint "${label}" is a ${type} checkpoint with output_fields — only prompt checkpoints can have them.`);
    return;
  }
  if (typeof cp.output_contract === 'string' && cp.output_contract.trim()) {
    error(`Checkpoint "${label}" has both output_fields and an output_contract — the contract already checks the reply, so remove one of them.`);
  }
  if (cp.review !== undefined && cp.review !== null) {
    error(`Checkpoint "${label}" has both output_fields and a Reviewer — a reviewer regeneration would bypass the check, so remove one of them.`);
  }
  Object.entries(fields as Record<string, unknown>).forEach(([name, raw]) => {
    if (!name.trim() || name !== name.trim() || name.includes('.')) {
      error(`Checkpoint "${label}" output_fields name "${name}" must be a plain JSON key (no dots, no surrounding spaces).`);
      return;
    }
    const field = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const { type: fieldType, format } = field;
    if (typeof fieldType === 'string' && fieldType && fieldType !== 'string' && fieldType !== 'string_list') {
      error(`Checkpoint "${label}" output_fields["${name}"].type "${fieldType}" is unknown — use "string", "string_list" or leave it out.`);
    }
    if (typeof format === 'string' && format && format !== 'emoji') {
      error(`Checkpoint "${label}" output_fields["${name}"].format "${format}" is unknown — use "emoji" or leave it out.`);
    }
    if (typeof format === 'string' && format && fieldType === 'string_list') {
      error(`Checkpoint "${label}" output_fields["${name}"].format "${format}" applies to a string, not a string_list.`);
    }
  });
}
